import React, { useState } from "react";

import {
  ClientReport,
  useDashboardReports,
  useReportEmailPreference,
} from "apps/company/api/rest/company-dashboard-reports";
import { useTranslation } from "lib/i18n/use-translation";

import { ACCOUNT_MANAGER } from "../contact";
import ReportEmailToggle from "./ReportEmailToggle";
import ReportViewer from "./ReportViewer";
import styles from "./reports-library.module.scss";

const K = "admin:companyDashboard.reportsLibrary";

type Props = {
  onViewed?: (report: ClientReport) => void;
  onOpened?: (report: ClientReport) => void;
  onDownloaded?: (report: ClientReport) => void;
  onContactClicked?: () => void;
  onEmailsToggled?: (enabled: boolean) => void;
};

const ReportsLibraryTab = ({
  onViewed,
  onOpened,
  onDownloaded,
  onContactClicked,
  onEmailsToggled,
}: Props) => {
  const { t } = useTranslation(["admin"]);
  const { reports, retry } = useDashboardReports();
  // Asked for alongside the reports rather than once they arrive.
  const emails = useReportEmailPreference(onEmailsToggled);
  const [pickedId, setPickedId] = useState<number | null>(null);

  const list = reports.state === "ready" ? reports.reports : [];
  const selected = list.find((r) => r.id === pickedId) ?? list[0];
  // A view is the admin choosing a report, whether or not its preview loads;
  // the report shown on arrival is not one.
  const pick = (report: ClientReport) => {
    if (report.id === pickedId) return;
    setPickedId(report.id);
    onViewed?.(report);
  };
  const period = (r: { quarter: number; year: number }) =>
    t(`${K}.period`, { quarter: r.quarter, year: r.year });

  return (
    <div className={styles["reports-library"]}>
      <header>
        <p className={styles["eyebrow"]}>{t(`${K}.eyebrow`)}</p>
        <h2 className={styles["heading"]}>{t(`${K}.title`)}</h2>
        <p className={styles["subheading"]}>{t(`${K}.subtitle`)}</p>
      </header>

      {reports.state === "loading" && (
        <p role="status" className={styles["status"]}>
          {t(`${K}.loading`)}
        </p>
      )}

      {reports.state === "error" && (
        <div role="alert" className={styles["status"]}>
          <p>{t(`${K}.error`)}</p>
          <button type="button" className={styles["retry"]} onClick={retry}>
            {t(`${K}.retry`)}
          </button>
        </div>
      )}

      {reports.state === "ready" && !selected && (
        <section className={styles["empty"]}>
          <p className={styles["eyebrow"]}>{t(`${K}.empty.eyebrow`)}</p>
          <h3 className={styles["empty-title"]}>{t(`${K}.empty.title`)}</h3>
          <p className={styles["empty-body"]}>{t(`${K}.empty.body`)}</p>
        </section>
      )}

      {selected && (
        <div className={styles["layout"]}>
          <div className={styles["list-column"]}>
            <p className={styles["eyebrow"]}>{t(`${K}.allDocuments`)}</p>
            <ul className={styles["list"]} aria-label={t(`${K}.listLabel`)}>
              {list.map((report) => (
                <li key={report.id}>
                  <button
                    type="button"
                    className={styles["item"]}
                    aria-current={
                      report.id === selected.id ? "true" : undefined
                    }
                    onClick={() => pick(report)}
                  >
                    <span className={styles["item-period"]}>
                      {period(report)}
                    </span>
                    <span className={styles["item-title"]}>{report.title}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <section
            className={styles["panel"]}
            aria-label={t(`${K}.selectedLabel`)}
          >
            <ReportViewer
              key={selected.id}
              report={selected}
              period={period(selected)}
              onOpened={onOpened}
              onDownloaded={onDownloaded}
            />
          </section>
        </div>
      )}

      {reports.state !== "loading" && <ReportEmailToggle {...emails} />}

      {reports.state !== "loading" && (
        <section className={styles["manager"]}>
          <p className={styles["eyebrow"]}>
            {t(`${K}.accountManager.eyebrow`)}
          </p>
          <p className={styles["manager-name"]}>{ACCOUNT_MANAGER.name}</p>
          <a
            className={styles["manager-email"]}
            href={`mailto:${ACCOUNT_MANAGER.email}`}
            onClick={onContactClicked}
          >
            {ACCOUNT_MANAGER.email}
          </a>
        </section>
      )}
    </div>
  );
};

export default ReportsLibraryTab;
